import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import enviro from 'enviro';
import { KOALA } from 'HubStyleTokens/colors'; // @ts-expect-error cannot find  typed file

import VidyardVideoPlayer from 'ui-addon-video-player/components/VidyardVideoPlayer';
import HubSpotVideoPlayer from 'ui-addon-video-player/components/HubSpotVideoPlayer';
import { TrackingContentTypes } from '../constants/Tracking';
import UsageTracker from '../utils/UsageTracker';
import { updateContentProgress } from '../features/userProgress'; // @ts-expect-error cannot find  typed file

import ContentViewport from '../components/ContentViewport';
import { videoReferenceType } from '../constants/ContentConstants';
import { useFullLesson } from '../features/lessons';
import { useFullTrack } from '../features/tracks';
const HS_VIDEO_CURRENT_TIME_TO_UPDATE_PROGRESS = 3;
/*
HS video requires region prop to render videos that are not for the same reason as that of the creator's portal ID.
without the region prop, if the video uploader's portal ID is in NA1, the video won't render in EU1 and vice versa.
*/

const HS_PROFESSOR_HUBLET = 'na1';

const useVideo = (contentId, lessonId, trackId) => {
  const {
    lesson
  } = useFullLesson(Number(lessonId));
  const {
    track
  } = useFullTrack(Number(trackId));
  const video = useMemo(() => lesson && lesson.content.find(content => content.id === Number(contentId)) || track && track.introVideo, [contentId, lesson, track]); // @ts-expect-error cannot find app in typed file

  const autoPlay = useSelector(state => state.app.video.autoPlay); // @ts-expect-error cannot find app in typed file

  const playSpeed = useSelector(state => state.app.video.playSpeed);
  const {
    language: videoLanguage,
    reference: videoId,
    referenceType: videoType,
    hubId: creatorHubId
  } = video || {};
  let PlayerComponent = VidyardVideoPlayer;
  if (videoType === videoReferenceType.HUBSPOT_VIDEO) PlayerComponent = HubSpotVideoPlayer;else PlayerComponent = VidyardVideoPlayer;
  return {
    PlayerComponent,
    videoType,
    videoId,
    videoLanguage,
    autoPlay,
    playSpeed,
    creatorHubId
  };
};

const StoredVideoContainer = ({
  contentId,
  lessonName,
  lessonId,
  trackName,
  trackId,
  onFinish,
  children
}) => {
  const {
    PlayerComponent,
    videoType,
    videoId,
    videoLanguage,
    autoPlay,
    playSpeed,
    creatorHubId
  } = useVideo(contentId, lessonId, trackId);
  const [hasClickedPlayForVideo, setHasClickedPlayForVideo] = useState(false);
  const [hasMarkedProgress, setHasMarkedProgress] = useState(false);
  const buildWhatExtraJson = useCallback(action => ({
    action,
    contentId,
    contentLanguage: videoLanguage,
    contentType: TrackingContentTypes.VIDEO,
    trackId,
    trackName,
    lessonName,
    lessonId,
    playerType: videoType
  }), [contentId, lessonId, lessonName, trackId, trackName, videoLanguage, videoType]);
  const handleVideoPlay = useCallback(() => {
    if (!hasClickedPlayForVideo) {
      setHasClickedPlayForVideo(true);
      UsageTracker.track('contentInteraction', buildWhatExtraJson('video-play'));
    }
  }, [buildWhatExtraJson, hasClickedPlayForVideo]);
  const handleUpdateProgress = useCallback(() => {
    UsageTracker.track('activation', buildWhatExtraJson('video-activation'));
    UsageTracker.track('contentProgress', buildWhatExtraJson('video-progression'));
    if (videoLanguage) // eslint-disable-next-line @typescript-eslint/no-floating-promises
      updateContentProgress({
        contentId: Number(contentId),
        lessonId: Number(lessonId),
        trackId: Number(trackId),
        language: videoLanguage
      });
  }, [buildWhatExtraJson, contentId, lessonId, trackId, videoLanguage]); //HS video uses progress event (https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/progress_event) to return current time of a video.
  //The progress event is triggered every time when a video's buffer level is updated.

  const handleProgressHsVideo = useCallback(e => {
    if (e && e.detail && e.detail.currentTime >= HS_VIDEO_CURRENT_TIME_TO_UPDATE_PROGRESS && !hasMarkedProgress) {
      setHasMarkedProgress(true);
      handleUpdateProgress();
    }
  }, [handleUpdateProgress, hasMarkedProgress]);
  const handleVideoFinish = useCallback(() => {
    UsageTracker.track('contentInteraction', buildWhatExtraJson('video-finished'));

    if (onFinish) {
      onFinish();
      if (!hasMarkedProgress) handleUpdateProgress();
    }
  }, [buildWhatExtraJson, handleUpdateProgress, hasMarkedProgress, onFinish]);
  const onProgress = videoType === videoReferenceType.VIDYARD ? handleUpdateProgress : handleProgressHsVideo;
  useEffect(() => {
    if (videoId && videoType === videoReferenceType.HUBSPOT_VIDEO) setHasMarkedProgress(false);
  }, [videoId, videoType]);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(ContentViewport, {
      children: /*#__PURE__*/_jsx(PlayerComponent, {
        autoPlay: autoPlay,
        env: enviro.isQa() ? 'qa' : 'prod',
        loadingBackground: KOALA,
        onFinish: handleVideoFinish,
        onPlay: handleVideoPlay,
        onUpdateProgress: onProgress,
        playbackRate: playSpeed / 100,
        portalId: creatorHubId,
        preload: "auto",
        region: HS_PROFESSOR_HUBLET,
        videoId: videoId,
        videoPlaySpeed: playSpeed
      }, contentId)
    }), children]
  });
};

export default StoredVideoContainer;